import { BadgeCheck, Blend, ListChecks, ListOrdered, PanelBottom, Ribbon, Square, UserRound } from 'lucide-react';

import { AdComponent, base, margin, shape, text, unit } from './shared';

/** Klocki uzupełniające: listy, plakietki, wstążki, stopki, podpisy, kroki. */
export const EXTRA_COMPONENTS: AdComponent[] = [

  {
    id: 'benefits',
    label: 'Lista korzyści',
    description: 'Trzy punkty z kropkami',
    icon: ListChecks,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.45);
      const fontSize = Math.round(u * 0.03);
      const step = Math.round(fontSize * 2.2);
      const dot = Math.round(fontSize * 0.5);

      const items = [
        'Zamówienia z pokoju bez telefonu',
        'Spa i restauracja w jednym miejscu',
        'Płatność kartą przy zamówieniu',
      ];

      return items.flatMap((item, index) => [
        shape(`Kropka ${index + 1}`, {
          shape: 'ellipse',
          x,
          y: y + step * index + Math.round(fontSize * 0.35),
          width: dot,
          height: dot,
          fill: palette.accent,
        }),
        text(`Korzyść ${index + 1}`, {
          text: item,
          x: x + dot * 2,
          y: y + step * index,
          width: width - dot * 2,
          fontSize,
          fontFamily: fonts.body,
          fontWeight: 500,
          color: palette.light,
        }),
      ]);
    },
  },

  {
    id: 'badge',
    label: 'Plakietka',
    description: 'Mała pigułka, np. „Nowość"',
    icon: BadgeCheck,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const fontSize = Math.round(u * 0.024);

      return [
        text('Plakietka', {
          text: 'Nowość',
          x: margin(scene),
          y: Math.round(scene.height * 0.14),
          width: Math.round(scene.width * 0.28),
          fontSize,
          fontFamily: fonts.badge,
          fontWeight: 700,
          uppercase: true,
          align: 'center',
          letterSpacing: Math.round(u * 0.004),
          color: palette.dark,
          background: palette.accent,
          backgroundRadius: 999,
          padding: Math.round(fontSize * 0.7),
        }),
      ];
    },
  },

  {
    id: 'ribbon',
    label: 'Wstążka narożna',
    description: 'Ukośny pasek w rogu planszy',
    icon: Ribbon,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const width = Math.round(u * 0.6);
      const height = Math.round(u * 0.09);
      const x = Math.round(scene.width - width * 0.72);
      const y = Math.round(u * 0.06);

      return [
        shape('Wstążka', {
          x,
          y,
          width,
          height,
          rotation: 45,
          fill: palette.accent,
        }),
        text('Napis wstążki', {
          text: '-25%',
          x,
          y: y + Math.round(height * 0.22),
          width,
          fontSize: Math.round(u * 0.04),
          fontFamily: fonts.badge,
          fontWeight: 900,
          rotation: 45,
          align: 'center',
          uppercase: true,
          color: palette.dark,
        }),
      ];
    },
  },

  {
    id: 'footer',
    label: 'Stopka marki',
    description: 'Cienka linia i adres strony',
    icon: PanelBottom,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.88);

      return [
        shape('Linia stopki', {
          x,
          y,
          width,
          height: Math.max(2, Math.round(u * 0.004)),
          fill: palette.accent,
          opacity: 0.6,
        }),
        text('Adres strony', {
          text: 'hotelspot.pl',
          x,
          y: y + Math.round(u * 0.03),
          width,
          fontSize: Math.round(u * 0.026),
          fontFamily: fonts.body,
          fontWeight: 700,
          letterSpacing: Math.round(u * 0.003),
          color: palette.light,
        }),
      ];
    },
  },

  {
    id: 'avatar-row',
    label: 'Autor wpisu',
    description: 'Awatar, imię i rola obok siebie',
    icon: UserRound,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.8);
      const avatar = Math.round(u * 0.1);

      return [
        {
          ...base('Awatar autora'),
          type: 'image',
          src: '',
          x,
          y,
          width: avatar,
          height: avatar,
          radius: 0,
          fit: 'cover',
          circle: true,
        },
        text('Imię', {
          text: 'Anna Kowalska',
          x: x + avatar + Math.round(u * 0.03),
          y,
          width: Math.round(scene.width * 0.5),
          fontSize: Math.round(u * 0.028),
          fontFamily: fonts.body,
          fontWeight: 700,
          color: palette.light,
        }),
        text('Rola', {
          text: 'Manager recepcji',
          x: x + avatar + Math.round(u * 0.03),
          y: y + Math.round(u * 0.045),
          width: Math.round(scene.width * 0.5),
          fontSize: Math.round(u * 0.024),
          fontFamily: fonts.body,
          fontWeight: 400,
          color: palette.accent,
        }),
      ];
    },
  },

  {
    id: 'steps',
    label: 'Trzy kroki',
    description: 'Numerowana ścieżka wdrożenia',
    icon: ListOrdered,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.42);
      const circle = Math.round(u * 0.075);
      const step = Math.round(circle * 1.9);
      const labels = ['Podpinamy obiekt', 'Wgrywamy menu i usługi', 'Gość skanuje kod'];

      return labels.flatMap((label, index) => [
        shape(`Koło ${index + 1}`, {
          shape: 'ellipse',
          x,
          y: y + step * index,
          width: circle,
          height: circle,
          fill: palette.accent,
        }),
        text(`Numer kroku ${index + 1}`, {
          text: String(index + 1),
          x,
          y: y + step * index + Math.round(circle * 0.24),
          width: circle,
          fontSize: Math.round(circle * 0.5),
          fontFamily: fonts.badge,
          fontWeight: 900,
          align: 'center',
          color: palette.dark,
        }),
        text(`Krok ${index + 1}`, {
          text: label,
          x: x + Math.round(circle * 1.4),
          y: y + step * index + Math.round(circle * 0.22),
          width: width - Math.round(circle * 1.4),
          fontSize: Math.round(u * 0.03),
          fontFamily: fonts.body,
          fontWeight: 500,
          color: palette.light,
        }),
      ]);
    },
  },

  {
    id: 'scrim',
    label: 'Przyciemnienie',
    description: 'Pas pod tekst na zdjęciu',
    icon: Blend,
    build: ({ scene, palette }) => {
      const height = Math.round(scene.height * 0.45);

      return [
        shape('Przyciemnienie', {
          x: 0,
          y: scene.height - height,
          width: scene.width,
          height,
          fill: palette.dark,
          opacity: 0.62,
        }),
      ];
    },
  },

  {
    id: 'logo-lockup',
    label: 'Znak marki',
    description: 'Kwadratowy znak i nazwa obok',
    icon: Square,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.12);
      const mark = Math.round(u * 0.075);

      return [
        shape('Znak', {
          x,
          y,
          width: mark,
          height: mark,
          fill: palette.accent,
          radius: Math.round(mark * 0.28),
        }),
        text('Nazwa marki', {
          text: 'Hotelspot',
          x: x + Math.round(mark * 1.35),
          y: y + Math.round(mark * 0.2),
          width: Math.round(scene.width * 0.5),
          fontSize: Math.round(mark * 0.55),
          fontFamily: fonts.headline,
          fontWeight: 800,
          letterSpacing: Math.round(u * 0.002),
          color: palette.light,
        }),
      ];
    },
  },
];
